import { useEffect, useRef, useState } from 'react';
import { Copy, Check } from 'lucide-react';

interface CopyButtonProps {
  /** The text that will be written to the clipboard. */
  value: string;
  /** Lazily resolve the text at click-time. Takes precedence over `value`
   *  when provided — use this when the source data may change between
   *  render and click (e.g. an editor's current contents). */
  getValue?: () => string;
  /** Optional label rendered next to the icon. Omit for icon-only. */
  label?: string;
  /** Label shown while in the "copied" state. Defaults to "Copied". */
  successLabel?: string;
  /** Additional classes to merge onto the button. */
  className?: string;
  /** Tooltip / aria title. Defaults to "Copy to clipboard". */
  title?: string;
  /** Icon size. 'sm' = h-3.5, 'md' = h-4. Defaults to 'sm'. */
  size?: 'sm' | 'md';
  /** Whether to stopPropagation on click. Defaults to true so the button
   *  is safe to embed inside a clickable row. */
  stopPropagation?: boolean;
  /** Fired after a successful copy. */
  onCopied?: () => void;
}

const RESET_MS = 1500;

/**
 * If `value` is a JSON object or array, return it pretty-printed with 2-space
 * indentation. Otherwise return it unchanged. The `{`/`[` prefix check avoids
 * reformatting plain strings or IDs that happen to be valid JSON literals.
 */
function formatForClipboard(value: string): string {
  const trimmed = value.trim();
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      return JSON.stringify(JSON.parse(trimmed), null, 2);
    } catch {
      // Not valid JSON — fall through and copy verbatim.
    }
  }
  return value;
}

/**
 * Small copy-to-clipboard button with a check-mark animation on success.
 * Designed to be embedded inline next to IDs, URLs, and code blocks.
 */
export function CopyButton({
  value,
  getValue,
  label,
  successLabel = 'Copied',
  className = '',
  title = 'Copy to clipboard',
  size = 'sm',
  stopPropagation = true,
  onCopied,
}: CopyButtonProps) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    if (stopPropagation) e.stopPropagation();
    try {
      const resolved = getValue ? getValue() : value;
      await navigator.clipboard.writeText(formatForClipboard(resolved));
      setCopied(true);
      onCopied?.();
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopied(false), RESET_MS);
    } catch {
      // Clipboard API unavailable (e.g. insecure context) — silently ignore.
    }
  };

  const iconClass = size === 'md' ? 'h-4 w-4' : 'h-3.5 w-3.5';

  return (
    <button
      type="button"
      onClick={handleClick}
      title={copied ? successLabel : title}
      aria-label={copied ? successLabel : title}
      data-testid="copy-button"
      data-copied={copied ? 'true' : 'false'}
      className={`focus-ring inline-flex items-center gap-1 rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors ${className}`}
    >
      {copied ? (
        <Check className={`${iconClass} text-green-500`} />
      ) : (
        <Copy className={iconClass} />
      )}
      {label && <span className="text-xs">{copied ? successLabel : label}</span>}
    </button>
  );
}
